
import {Period} from '@/util/period';

export enum TimeCursorType {
  LIMIT_MARKER,
  LIMIT_DRAG
}

export enum TimeCursorMode {
  OFF,
  AUTHORITIES,
  PARKABILITY
}

/**
 *
 * @class TimeCursor
 *
 * @brief this class holds the current state of a time cursor/indicator for the
 * time axis.  Cursors are intended to have a position on the time timeline,
 * and be on or off.  When a cursor is on, it can be in one of several pre-defined
 * modes.  Clicking the cursor is intended to cycle from off to on/first mode, then
 * on/second mode, etc, cycling back to off.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      May 2021
 *
 */
export class TimeCursor {

  /** the kind of cursor */
  public _type: TimeCursorType = TimeCursorType.LIMIT_MARKER;

  /** the mode of the cursor */
  public _mode: number         = 0;

  /** the current screen position/time in the time axis canvas */
  public _x: number            = 0;

  /** the tip vertically of the indicator */
  public _y: number            = 0;

  /** true if we are being hovered */
  public _hovering: boolean    = false;

  /** the cursor color (for each mode */
  public _colors: Map<TimeCursorMode, string> = new Map<TimeCursorMode, string>();

  /** the mode cycle order */
  public _modes: TimeCursorMode[] = [
    TimeCursorMode.OFF,
    TimeCursorMode.AUTHORITIES,
    TimeCursorMode.PARKABILITY
  ];

  /* the polygon we use for the cursor */
  public _poly: [number, number][] = [
    [.5,  .375],
    [.5,  .75],
    [-.5, .75],
    [-.5, .375]
  ];

  /** the scaled up cursor size (max on the width side) */
  public _scale: number        = 16;

  /** cursor width */
  public _width: number        = 0;

  /** cursor height */
  public _height: number       = 0;

  /** the current world time for this position in the axis (x projected into world view) */
  public _wt: number           = 0;

  /** string (human readable) version of the position */
  public _text: string         = '<no time>';

  /** standard constructor, normally only ever called by TimeAxis */
  constructor(
    type: TimeCursorType,
    mode: TimeCursorMode,
  ) {

    this._type   = type;
    this._mode   = 0;

    this._width  = this._scale * (Math.max(...this._poly.map((x: [number, number]) => { return x[0];})) * 2);
    this._height = this._scale * (Math.max(...this._poly.map((y: [number, number]) => { return y[1];})));

    this._y      = this._height + 2;

    this._colors.set(TimeCursorMode.OFF,         '#999999');
    this._colors.set(TimeCursorMode.AUTHORITIES, '#ffff00');
    this._colors.set(TimeCursorMode.PARKABILITY, '#33cc33');

    this.moveTo(this._x, this._wt, Period.fromSeconds(this._wt*3600.0).asString());
  }

  /** check to see if this canvas point falls inside this cursor graphic */
  contains(x: number): boolean {

    const left:  number = this._x - (this._width / 2.0) - 3;
    const right: number = this._x + (this._width / 2.0) + 3;

    if ((x >= left) && (x <= right)) {

      return true;
    }

    return false;
  }

  /** move the indicator to this position on the time time line */
  moveTo(x: number, wt: number, text: string) {

    let mt = '';

    switch(this.mode()) {

      case TimeCursorMode.OFF:         mt = 'OFF'; break;
      case TimeCursorMode.AUTHORITIES: mt = 'AUTHORITIES'; break;
      case TimeCursorMode.PARKABILITY: mt = 'PARKABILITY'; break;
    }

    this._text   = `${text} - ${mt}`;
    this._wt     = wt;
    this._x      = x;
  }

  /** draw this cursor */
  draw(context: CanvasRenderingContext2D): boolean {

    if (this._type == TimeCursorType.LIMIT_DRAG) {

      context.fillStyle = '#ffffff';

    } else if (this._hovering) {

      context.fillStyle = '#0000ff';

    } else {

      context.fillStyle = this._colors.get(this._modes[this._mode]);
    }

    context.beginPath();
    context.moveTo(this._x, this._y);

    this._poly.forEach((seg: [number, number]) => {

      context.lineTo(seg[0] * this._scale + this._x, seg[1] * -this._scale + this._y,);
    });

    context.closePath();
    context.fill();

    return true;
  }

  mode(): TimeCursorMode {

    return this._modes[this._mode];
  }

  click() {

    this._hovering = false;

    this._mode++;

    if (this._mode >= this._modes.length) {

      this._mode = 0;
    }

  }

}
